<template>
  <div v-if="internalAccount" class="account-form-wrap">
    <div class="settings-section">
      <div class="settings-section__header">
        <div class="section-icon section-icon--blue">
          <i class="el-icon-user"></i>
        </div>
        <h3 class="settings-section__title">{{ $t('settings.accountSectionINFO') }}</h3>
      </div>

      <el-form
        class="account-edit-form"
        ref="form"
        :rules="rules"
        :model="internalAccount"
        label-position="top">

        <div class="form-grid">
          <el-form-item :label="$t('base.firstName')" prop="firstName">
            <el-input v-model="internalAccount.firstName" prefix-icon="el-icon-user"></el-input>
          </el-form-item>

          <el-form-item :label="$t('base.appLoginName')" prop="appLoginName">
            <el-input v-model="internalAccount.appLoginName">
              <template slot="prepend" v-if="customer">{{ customer.appLoginDomain }}\</template>
            </el-input>
          </el-form-item>

          <el-form-item :label="$t('base.lastName')" prop="lastName">
            <el-input v-model="internalAccount.lastName" prefix-icon="el-icon-user"></el-input>
          </el-form-item>

          <el-form-item :label="$t('settings.authority')">
            <el-select v-model="internalAccount.userAuthority" :placeholder="$t('settings.authority')">
              <el-option
                v-for="authority in authorities"
                :label="authority"
                :key="authority"
                :value="authority">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item :label="$t('base.email')" prop="email">
            <el-input v-model="internalAccount.email" prefix-icon="el-icon-message"></el-input>
          </el-form-item>
        </div>

        <div class="form-actions">
          <el-button
            v-if="mode === 'edit'"
            type="primary"
            class="btn-submit"
            :loading="loading"
            @click="onSubmit">
            <i class="el-icon-check"></i> {{ $t('settings.save') }}
          </el-button>
          <el-button
            v-if="mode === 'create'"
            type="success"
            class="btn-submit"
            :loading="loading"
            @click="onSubmit">
            <i class="el-icon-plus"></i> {{ $t('settings.create') }}
          </el-button>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import {generateRules} from "@/helper/AccountSettingsHelper";

export default {
  name: "AccountForm",
  props: {
    loading: {default: false},
    account: {default: null},
    mode: {default: 'edit'},
    customer: {default: null},
  },
  beforeMount() {
    this.rules = generateRules(this.$t);
  },
  mounted() {
    if (this.mode === 'create') {
      this.internalAccount = {userAuthority: 'USER'};
    }
  },
  data() {
    return {
      internalAccount: null,
      rules: {},
      authorities: ['ADMIN', 'REPORTER', 'USER'],
    };
  },
  watch: {
    account: {
      immediate: true,
      handler() {
        this.internalAccount = {...this.account};
        if (this.customer) {
          const customerDomainRegex = new RegExp(`${this.customer.appLoginDomain}\\\\`, "i");
          if (this.internalAccount.appLoginName) {
            this.internalAccount.appLoginName =
              this.internalAccount.appLoginName.replace(customerDomainRegex, '');
          }
        }
      }
    }
  },
  methods: {
    onSubmit() {
      this.$refs['form'].validate((valid) => {
        if (valid) {
          const resultAccount = {...this.internalAccount};
          const customerDomainRegex = new RegExp(`${this.customer.appLoginDomain}\\\\`, "i");
          if (!customerDomainRegex.test(resultAccount.appLoginName)) {
            resultAccount.appLoginName =
              `${this.customer.appLoginDomain.toLowerCase()}\\${resultAccount.appLoginName}`;
          }
          this.$emit('submit', resultAccount);
        }
      });
    },
  }
};
</script>

<style scoped lang="scss">
$border: #e8e8ee;
$text-primary: #1a1a2e;
$text-secondary: #606266;
$primary: #409EFF;

.section-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    color: #fff;
    font-size: 15px;
  }

  &--blue {
    background: linear-gradient(135deg, #409EFF, #2d7dd2);
  }
}

.settings-section {
  background: #fff;
  border: 1px solid $border;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .04);

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 20px;
    border-bottom: 1px solid $border;
    background: #f8f9fc;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: $text-primary;
  }
}

.account-edit-form {
  padding: 24px 20px 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 24px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
}

:deep(.el-form-item__label) {
  font-size: 13px;
  font-weight: 500;
  color: $text-secondary;
  padding-bottom: 6px;
  line-height: 1.4;
}

:deep(.el-input__inner) {
  border-radius: 8px;
  border-color: $border;
  height: 40px;
  font-size: 13.5px;
  color: $text-primary;
  transition: border-color .2s, box-shadow .2s;

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba(64, 158, 255, .12);
  }
}

:deep(.el-input-group__prepend) {
  border-radius: 8px 0 0 8px;
  background: #f5f7fa;
  color: $text-secondary;
  font-size: 13px;
  border-color: $border;
}

:deep(.el-input-group--prepend .el-input__inner) {
  border-radius: 0 8px 8px 0;
}

:deep(.el-select) {
  width: 100%;
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}

.form-actions {
  padding-top: 4px;
}

.btn-submit {
  border-radius: 8px;
  font-weight: 500;
  min-width: 110px;
}
</style>
